//
// # Schematic Ports and Pins
//

// NPM Imports
import typia from "typia";
import type { SignalType } from "GenAlphaSchema";

// Local Imports
import { exhaust, Validator } from "../errors";
import { ScalarOrBusName } from "./name";
import { Polygon, Element } from "../graphics/element";
import type { SymbolPin } from "./symbol";
import { Bbox, Point, Xform, Orientation } from "../core";
import { TextFormat, TextOrientation } from "../graphics/text";
import { PortDir } from "./portdir";
import { SignalUsage, ConnKind } from "./conns_data";

// Type alias for valid port names, which can be either scalars or buses.
export type ValidPortName = ScalarOrBusName;
export const ValidPortName = ScalarOrBusName;

// # Schematic Port
export interface SchPort {
  connKind: ConnKind.Signal; // Connection Kind Discriminator
  name: ValidPortName; // Port Name
  dir: PortDir; // Port Kind/ Direction. Reserved for `SchPort` variants.
  usage: SignalUsage; // Signal Usage
}
export const SchPort = {
  new: (name: ValidPortName, dir: PortDir = PortDir.Inout): SchPort => {
    return {
      connKind: ConnKind.Signal,
      name,
      dir,
      usage: SignalUsage.Signal, // FIXME!
    };
  },

  // Change the `PortDir` of a `port`.
  updateKind: (port: SchPort, dir: PortDir) => {
    port.dir = dir;
    const symbol = SchPinSymbol.get(dir);
    // for (const pin of port.pins) {
    //   pin.labelFmt = TextFormat.clone(symbol.defaultLabelFmt);
    // }
  },
};

// # Schematic Pin
export type SchPin = SchPinData & SchPinInner;

export type SchPinSpec = SchPinData & { portName: string; direction: PortDir };

export const SchPinSpec = {
  // Create a new `SchPinSpec`
  new: (
    portName: string,
    dir: PortDir,
    loc: Point = Point.origin(),
    orientation: Orientation = Orientation.default(),
    labelFmt: TextFormat | undefined = undefined
  ): SchPinSpec => {
    const symbol = SchPinSymbol.get(dir);
    const labelFmt_ = labelFmt ?? TextFormat.clone(symbol.defaultLabelFmt);
    return {
      portName,
      direction: dir,
      signalType: "signal",
      xform: Xform.fromParts(loc, orientation), // FIXME: probably make these constructor args instead
      labelFmt: labelFmt_,
    };
  },

  default: (): SchPinSpec => SchPinSpec.new("pin", PortDir.Input),

  clone: (pin: SchPinSpec): SchPinSpec => {
    const { portName, direction, xform, labelFmt, signalType } = pin;
    return {
      portName,
      direction,
      signalType,
      xform: Xform.clone(xform),
      labelFmt: TextFormat.clone(labelFmt),
    };
  },

  // JSON parser & validator
  validator: new Validator(typia.createValidate<SchPinSpec>()),
  // JSON stringifier
  stringify: typia.json.createStringify<SchPinSpec>(),
};

export interface SchPinData {
  signalType?: SignalType;
  xform: Xform; // Transform
  labelFmt: TextFormat; // Label Format. FIXME: required or nah?
  id?: number;
}

// # Schematic Pin Inner/ Derived Fields
export interface SchPinInner {
  //
  port: SchPort; // Parent Port
  symbol: SchPinSymbol; // Symbol
  bbox: Bbox; // Bounding Box
}

export const SchPin = {
  Kind: PortDir,

  // Create a new `SchPin`
  new: (port: SchPort, pinData: SchPinData): SchPin => {
    // The key here: get the `SchPinSymbol` based on `kind`
    const symbol = SchPinSymbol.get(port.dir);
    // Apply the default label format if one wasn't provided.
    // FIXME: is this a thing?
    const labelFmt =
      pinData.labelFmt ?? TextFormat.clone(symbol.defaultLabelFmt);
    const { xform } = pinData;
    return {
      port,
      signalType: pinData.signalType,
      xform,
      labelFmt,
      symbol,
      bbox: Bbox.empty(),
    };
  },

  clone: (pin: SchPin): SchPin => {
    const { xform, labelFmt, symbol, bbox } = pin;
    return {
      ...pin,
      labelFmt: TextFormat.clone(labelFmt),
      xform: Xform.clone(xform),
      symbol, // Note: not cloned
      bbox: Bbox.clone(bbox),
    };
  },

  // Pull the `Spec` parts out of a `SchPin`.
  toSpec: (pin: SchPin): SchPinSpec => {
    const { port, labelFmt, xform } = pin;
    const { name, dir } = port;
    return {
      portName: name.repr(),
      direction: dir,
      signalType: pin.signalType,
      labelFmt: TextFormat.clone(labelFmt),
      xform: Xform.clone(xform),
    };
  },

  // Update the `PortDir` of a `SchPin`.
  // Largely forwards to the parent `SchPort`.
  updateKind: (pin: SchPin, dir: PortDir) => {
    return SchPort.updateKind(pin.port, dir);
  },

  // Calculate the `Bbox` for `port`, updating its cached value.
  // FIXME: sorting out how & when we can reuse these values.
  calcBbox: (pin: SchPinSpec): Bbox => {
    const { direction, xform } = pin;

    // Get its symbol definition, for its black box
    const schPortSymbol = SchPinSymbol.get(direction);
    const symbolBbox = schPortSymbol.bbox;
    const bbox = Xform.transformBbox(xform, symbolBbox);

    // Return the new bbox
    return bbox;
  },

  // JSON parser & validator
  validator: new Validator(typia.createValidate<SchPinSpec>()),
  // JSON stringifier
  stringify: typia.json.createStringify<SchPinSpec>(),
};

//
// # Schematic Pin Symbols
//
// The definitions of the (three) elements instantiable as schematic `Port`s.
//

// # Schematic Pin Symbol (Spec)
//
// Simplified version of the `Symbol` type, for use in `SchPin`.
// Both the type-definition and the library of `SchPinSymbol`s are defined in this module;
// Only the `SchPinSymbol` union of defined values is exported.
//
interface SchPinSymbolSpec {
  //
  dir: PortDir; // Port Direction
  symbolPin: SymbolPin; // (Single) Port Connection Location
  graphics: Array<Element>; // Drawing Elements
  selectionBox: Bbox; // Selection Box
  defaultLabelFmt: TextFormat; // Default Name-Label Format
  bbox: Bbox; // Bounding Box
}

const inputPolygon = Polygon.fromTuples(
  [-20, -9],
  [-20, 9],
  [-9, 9],
  [0, 0],
  [-9, -9]
);

// Create the library of `SchPinSymbol`s.
export const Input: SchPinSymbolSpec = {
  dir: PortDir.Input,
  symbolPin: {
    portName: "Input",
    width: 1,
    direction: PortDir.Input,
    shape: inputPolygon,
  },
  graphics: [inputPolygon],
  selectionBox: {
    min: {
      x: -20,
      y: -10,
    },
    max: {
      x: 0,
      y: 10,
    },
  },
  bbox: {
    min: {
      x: -20,
      y: -10,
    },
    max: {
      x: 0,
      y: 10,
    },
  },
  defaultLabelFmt: TextFormat.fromPartial({
    loc: { x: -30, y: 0 },
    ...TextOrientation.fromPartial({
      alignment: "right",
      baseline: "middle",
    }),
  }),
};

const outputPolygon = Polygon.fromTuples(
  [0, -9],
  [0, 9],
  [11, 9],
  [20, 0],
  [11, -9]
);

export const Output: SchPinSymbolSpec = {
  dir: PortDir.Output,
  symbolPin: {
    portName: "Output",
    width: 1,
    direction: PortDir.Output,
    shape: outputPolygon,
  },
  graphics: [outputPolygon],
  selectionBox: {
    min: {
      x: 0,
      y: -10,
    },
    max: {
      x: 20,
      y: 10,
    },
  },
  bbox: {
    min: {
      x: 0,
      y: -10,
    },
    max: {
      x: 20,
      y: 10,
    },
  },
  defaultLabelFmt: TextFormat.fromPartial({
    loc: { x: 25, y: 0 },
    ...TextOrientation.fromPartial({
      alignment: "left",
      baseline: "middle",
    }),
  }),
};

const inoutPolygon = Polygon.fromTuples(
  [10, 0],
  [1, 9],
  [-11, 9],
  [-20, 0],
  [-11, -9],
  [1, -9]
);

export const Inout: SchPinSymbolSpec = {
  dir: PortDir.Inout,
  symbolPin: {
    portName: "Inout",
    width: 1,
    direction: PortDir.Inout,
    shape: inoutPolygon,
  },
  graphics: [inoutPolygon],
  selectionBox: {
    min: {
      x: -20,
      y: -10,
    },
    max: {
      x: 10,
      y: 10,
    },
  },
  bbox: {
    min: {
      x: -20,
      y: -10,
    },
    max: {
      x: 10,
      y: 10,
    },
  },
  defaultLabelFmt: TextFormat.fromPartial({
    loc: { x: -25, y: 0 },
    ...TextOrientation.fromPartial({
      alignment: "right",
      baseline: "middle",
    }),
  }),
};

// # Schematic Port Symbol
//
// Simplified version of the `Symbol` type, for use in `SchPin`.
// The union-type of these three values is the set of all `SchPinSymbol`s.
//
export type SchPinSymbol = typeof Input;

export const SchPinSymbol = {
  // Get the `SchPinSymbol` of port-direction `dir`
  get: (dir: PortDir): SchPinSymbol => {
    switch (dir) {
      case PortDir.Input:
        return Input;
      case PortDir.Output:
        return Output;
      case PortDir.Inout:
        return Inout;
      default:
        exhaust(dir);
    }
  },
};
